import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Cart Item Types
export interface CartItem {
  id: string;
  productId: string;
  name: string;
  price: number;
  quantity: number;
  type: 'merchandise' | 'food' | 'ticket';
  size?: string;
  color?: string;
  imageUrl?: string;
  notes?: string;
  // Ticket-specific fields
  eventId?: string;
  eventName?: string;
  eventDate?: string;
  ticketType?: string;
}

interface CartStore {
  items: CartItem[];
  lastOrder: CartItem[];

  // Actions
  addItem: (item: Omit<CartItem, 'id'>) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  updateSize: (id: string, size: string) => void;
  updateColor: (id: string, color: string) => void;
  updateNotes: (id: string, notes: string) => void;
  clearCart: () => void;
  checkout: () => void;

  // Computed
  totalItems: () => number;
  subtotal: () => number;
  merchandiseItems: () => CartItem[];
  foodItems: () => CartItem[];
  ticketItems: () => CartItem[];
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      lastOrder: [],

      addItem: (item) => {
        const id = `cart-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`;

        // Check if same product with same options exists
        const existingIndex = get().items.findIndex(
          (i) =>
            i.productId === item.productId &&
            i.size === item.size &&
            i.color === item.color &&
            i.type === item.type
        );

        if (existingIndex >= 0) {
          // Update quantity of existing item
          set((state) => ({
            items: state.items.map((i, index) =>
              index === existingIndex
                ? { ...i, quantity: i.quantity + item.quantity }
                : i
            ),
          }));
        } else {
          // Add new item
          set((state) => ({
            items: [...state.items, { ...item, id }],
          }));
        }
      },

      removeItem: (id) => {
        set((state) => ({
          items: state.items.filter((item) => item.id !== id),
        }));
      },

      updateQuantity: (id, quantity) => {
        if (quantity <= 0) {
          get().removeItem(id);
          return;
        }
        set((state) => ({
          items: state.items.map((item) =>
            item.id === id ? { ...item, quantity } : item
          ),
        }));
      },

      updateSize: (id, size) => {
        set((state) => ({
          items: state.items.map((item) =>
            item.id === id ? { ...item, size } : item
          ),
        }));
      },

      updateColor: (id, color) => {
        set((state) => ({
          items: state.items.map((item) =>
            item.id === id ? { ...item, color } : item
          ),
        }));
      },

      updateNotes: (id, notes) => {
        set((state) => ({
          items: state.items.map((item) =>
            item.id === id ? { ...item, notes } : item
          ),
        }));
      },

      clearCart: () => set({ items: [] }),

      checkout: () => {
        set((state) => ({
          lastOrder: [...state.items],
          items: [],
        }));
      },

      totalItems: () => get().items.reduce((sum, item) => sum + item.quantity, 0),

      subtotal: () => {
        return get().items.reduce((sum, item) => sum + item.price * item.quantity, 0);
      },

      merchandiseItems: () => get().items.filter((item) => item.type === 'merchandise'),

      foodItems: () => get().items.filter((item) => item.type === 'food'),

      ticketItems: () => get().items.filter((item) => item.type === 'ticket'),
    }),
    {
      name: 'driveitnq-cart',
    }
  )
);

// Order Types for tracking purchases
export interface Order {
  id: string;
  orderNumber: string;
  items: CartItem[];
  subtotal: number;
  total: number;
  status: 'pending' | 'confirmed' | 'ready' | 'collected';
  type: 'merchandise' | 'food' | 'mixed';
  pickupLocation?: string;
  createdAt: string;
}

interface OrderStore {
  orderHistory: Order[];
  addOrder: (order: Omit<Order, 'id' | 'orderNumber' | 'createdAt'>) => string;
  updateOrderStatus: (id: string, status: Order['status']) => void;
  getOrder: (id: string) => Order | undefined;
  clearHistory: () => void;
}

export const useOrderStore = create<OrderStore>()(
  persist(
    (set, get) => ({
      orderHistory: [],

      addOrder: (order) => {
        const id = `ORD-${Date.now()}-${Math.random().toString(36).substr(2, 4).toUpperCase()}`;
        const orderNumber = String(Math.floor(1000 + Math.random() * 9000));
        const newOrder: Order = {
          ...order,
          id,
          orderNumber,
          createdAt: new Date().toISOString(),
        };
        set((state) => ({
          orderHistory: [newOrder, ...state.orderHistory],
        }));
        return id;
      },

      updateOrderStatus: (id, status) => {
        set((state) => ({
          orderHistory: state.orderHistory.map((order) =>
            order.id === id ? { ...order, status } : order
          ),
        }));
      },

      getOrder: (id) => {
        return get().orderHistory.find((order) => order.id === id);
      },

      clearHistory: () => set({ orderHistory: [] }),
    }),
    {
      name: 'driveitnq-orders',
    }
  )
);
